import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Popover,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Divider,
  Typography,
  Stack,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { t } from "i18next";

export interface FilterItem {
  id: string;
  field: string;
  operator: string;
  value: any;
}

interface CustomFilterPanelProps {
  filters: FilterItem[];
  onFiltersChange: (filters: FilterItem[]) => void;
  isTab1: boolean;
  activeDesignations?: Array<{ Code: string; Name: string }>;
  clearFilter: boolean
}



export const CertConnectCustomFilterModal: React.FC<CustomFilterPanelProps> = ({
  filters,
  onFiltersChange,
  isTab1,
  activeDesignations = [],
  clearFilter
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [localFilters, setLocalFilters] = useState<FilterItem[]>(filters);

  // Designation panel state
  const [designationPanelOpen, setDesignationPanelOpen] = useState<string | null>(null);
  const [designationPanelAnchor, setDesignationPanelAnchor] = useState<HTMLElement | null>(null);
  const [designationSearchTerm, setDesignationSearchTerm] = useState("");
  const [tempSelectedDesignations, setTempSelectedDesignations] = useState<string[]>([]);
  const [tempFilterMode, setTempFilterMode] = useState<"any" | "all">("any");

  const FIELD_OPTIONS_TAB1 = [
    { value: "FirstName", label: t("dataGrid.Fields.FirstName") },
    { value: "LastName", label: t("dataGrid.Fields.LastName") },
    { value: "ContactCCOID", label: t("dataGrid.Fields.CCOID") },
    { value: "MyCCODesignationDetails", label: t("dataGrid.Fields.Designation") },
    { value: "Available", label: t("dataGrid.Fields.Availability") },
    { value: "CertExpirationDate", label: t("dataGrid.Fields.ExpDate") },
    { value: "Note", label: t("dataGrid.Fields.Note") },
  ];

  const FIELD_OPTIONS_TAB2 = [
    { value: "FirstName", label: t("dataGrid.Fields.FirstName") },
    { value: "LastName", label: t("dataGrid.Fields.LastName") },
    { value: "ContactCCOID", label: t("dataGrid.Fields.CCOID") },
    { value: "ActionRequired", label: t("dataGrid.Fields.ActionRequired") },
  ];

  const OPERATOR_MAP: Record<string, Array<{ value: string; label: string }>> = {
    FirstName: [{ value: "contains", label: t("dataGrid.Operators.contains") }],
    LastName: [{ value: "contains", label: t("dataGrid.Operators.contains") }],
    ContactCCOID: [{ value: "contains", label: t("dataGrid.Operators.contains") }],
    MyCCODesignationDetails: [
      { value: "isAnyOf", label: t("dataGrid.Operators.MatchAny") },
      { value: "is", label: t("dataGrid.Operators.MatchAll") }
    ],
    Available: [{ value: "is", label: t("dataGrid.Operators.Is") }],
    CertExpirationDate: [
      { value: "on", label: t("dataGrid.Operators.On") },
      { value: "onOrBefore", label: t("dataGrid.Operators.OnOrBefore") },
      { value: "onOrAfter", label: t("dataGrid.Operators.OnOrAfter") },
    ],
    Note: [{ value: "contains", label: t("dataGrid.Operators.contains") }],
    ActionRequired: [{ value: "is", label: t("dataGrid.Operators.Is") }],
  };

  const fieldOptions = isTab1 ? FIELD_OPTIONS_TAB1 : FIELD_OPTIONS_TAB2;

  // 1. Calculate which fields are currently being used across all rows
  const usedFields = localFilters.map((f) => f.field);



  const handleOpenFilterMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilterMenu = () => {
    // Remove filters that have no value before closing
    const sanitized = localFilters.filter((f) => {
      const v = f.value;

      // Treat "null" (Availability: All) and empty values as not applied
      if ((v === "null" || v === null) && f.field == "Available") return true;
      if (v === "null" || v === null || v === undefined) return false;
      if (typeof v === "string" && v.trim() === "") return false;
      if (Array.isArray(v) && v.length === 0) return false;


      return true;
    });

    if (sanitized.length !== localFilters.length) {
      setLocalFilters(sanitized);
      onFiltersChange(sanitized);
    }

    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Handle designation panel open
  const handleOpenDesignationPanel = (event: React.MouseEvent<HTMLElement>, filterId: string, currentValue: any, currentOperator: string) => {
    setDesignationPanelAnchor(event.currentTarget);
    setDesignationPanelOpen(filterId);
    setDesignationSearchTerm("");

    // Set temp selections based on current value
    if (Array.isArray(currentValue)) {
      setTempSelectedDesignations(currentValue);
    } else {
      setTempSelectedDesignations([]);
    }

    // Set temp filter mode based on operator
    setTempFilterMode(currentOperator === "is" ? "all" : "any");
  };

  // Handle designation panel close
  const handleCloseDesignationPanel = () => {
    setDesignationPanelOpen(null);
    setDesignationPanelAnchor(null);
    setDesignationSearchTerm("");
  };

  // Handle toggle designation in temp state
  const toggleTempDesignation = (code: string) => {
    setTempSelectedDesignations((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // Handle apply designation filter
  const handleApplyDesignationFilter = (filterId: string) => {
    // Update both value and operator in a single operation
    const updated = localFilters.map((f) => {
      if (f.id === filterId) {
        return {
          ...f,
          value: tempSelectedDesignations,
          operator: tempFilterMode === "all" ? "is" : "isAnyOf"
        };
      }
      return f;
    });
    setLocalFilters(updated);
    handleCloseDesignationPanel();
  };

  // Add a new empty filter row
  const handleAddFilter = () => {
    // 2. Find the first field option that is NOT currently used
    const nextAvailableField = fieldOptions.find(
      (opt) => !usedFields.includes(opt.value)
    );

    // If all fields are used, do nothing (or we could disable the button)
    if (!nextAvailableField) return;

    const newFilter: FilterItem = {
      id: `filter-${Date.now()}`,
      field: nextAvailableField.value, // Use the available field
      operator: OPERATOR_MAP[nextAvailableField.value]?.[0]?.value || "contains",
      value: "",
    };
    const updated = [...localFilters, newFilter];
    setLocalFilters(updated);
  };

  // Remove a filter row
  const handleRemoveFilter = (filterId: string) => {
    const updated = localFilters.filter((f) => f.id !== filterId);
    setLocalFilters(updated);
  };

  // Update a filter property
  const handleUpdateFilter = (
    filterId: string,
    key: keyof FilterItem,
    newValue: any
  ) => {
    const updated = localFilters.map((f) => {
      if (f.id === filterId) {
        const updatedFilter = { ...f, [key]: newValue };

        // If field changed, reset operator and value
        if (key === "field") {
          updatedFilter.operator =
            OPERATOR_MAP[newValue]?.[0]?.value || "contains";
          updatedFilter.value = "";
        }

        return updatedFilter;
      }
      return f;
    });
    setLocalFilters(updated);
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setLocalFilters([]);
    onFiltersChange([]);
  };

  useEffect(() => {
    setLocalFilters([]);

  }, [clearFilter]);

  const handleApplyFilters = () => {
    const sanitized = localFilters.filter((f) => {
      const v = f.value;
      if ((v === "null" || v === null) && f.field == "Available") return true;
      if (v === "null" || v === null || v === undefined) return false;
      if (typeof v === "string" && v.trim() === "") return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    });

    setLocalFilters(sanitized);
    onFiltersChange(sanitized);
    setAnchorEl(null);
  };

  const filteredDesignations = React.useMemo(() => {
    const designations: { Code: string; Name: string }[] = activeDesignations || [];
    const term = designationSearchTerm.toLowerCase();
    return designations
      .filter((d) =>
        d.Code.toLowerCase().includes(term) || d.Name.toLowerCase().includes(term)
      )
      .sort((a, b) =>
        a.Code.localeCompare(b.Code, undefined, { sensitivity: "base" })
      );
  }, [activeDesignations, designationSearchTerm]);

  const renderDesignationPanelContent = (filterId: string) => {
    const noResults = filteredDesignations.length === 0;

    return (
      <>
        {/* <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <label style={{ fontWeight: 600, fontSize: "14px" }}>
            Match Mode
          </label>

        </Box> */}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
          {tempSelectedDesignations.map((code) => (
            <Chip
              key={code}
              label={code}
              size="small"
              onDelete={() => toggleTempDesignation(code)}
            />
          ))}
        </Box>

        <TextField
          size="small"
          placeholder={t("dataGrid.DesignationModal.SearchDesignation")}
          value={designationSearchTerm}
          onChange={(e) => setDesignationSearchTerm(e.target.value)}
          fullWidth
        />

        <Box
          sx={{
            mt: 1,
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {filteredDesignations.map((d) => (
            <FormControlLabel
              key={d.Code}
              sx={{ display: "flex" }}
              control={
                <Checkbox
                  sx={{ "&.Mui-checked": { color: "#c67b05" } }}
                  size="small"
                  checked={tempSelectedDesignations.includes(d.Code)}
                  onChange={() => toggleTempDesignation(d.Code)}
                />
              }
              label={
                <span style={{ fontSize: "13px" }}>
                  <b>{d.Code}</b> {d.Name}
                </span>
              }
            />
          ))}
        </Box>

        {noResults && (
          <Box
            sx={{
              textAlign: "center",
              mt: 1,
              color: "#999",
              fontSize: "13px",
            }}
          >
            {t("dataGrid.DesignationModal.NoDesignationsFound")}
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="contained"
            className="applyBtn"
            onClick={() => handleApplyDesignationFilter(filterId)}
            fullWidth
            sx={{
              backgroundColor: "#c67b05",
              "&:hover": {
                backgroundColor: "#a56604",
              },
            }}
          >
            {t("dataGrid.DesignationModal.Apply")}
          </Button>
        </Box>
      </>
    );
  };

  const renderValueInput = (filter: FilterItem) => {
    const { field, operator, value, id } = filter;

    switch (field) {
      case "Available":
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>{t("dataGrid.Value")}</InputLabel>
            <Select
              label={t("dataGrid.Value")}
              value={value ?? "null"}
              onChange={(e) =>
                handleUpdateFilter(id, "value", e.target.value)
              }
            >
              <MenuItem value="null">{t("dataGrid.All")}</MenuItem>
              <MenuItem value="true">{t("dataGrid.Available")}</MenuItem>
              <MenuItem value="false">{t("dataGrid.NotAvailable")}</MenuItem>
            </Select>
          </FormControl>
        );

      case "MyCCODesignationDetails":
        const designations: { Code: string; Name: string }[] = activeDesignations || [];
        const currentValues = Array.isArray(value) ? value : [];
        const displayValue = currentValues.length > 0
          ? `${currentValues.length} selected`
          : t("dataGrid.SelectDesignations");

        return (
          <Box sx={{ position: "relative" }}>
            <TextField
              size="small"
              label={t("dataGrid.Designation")}
              value={displayValue}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenDesignationPanel(e, id, value, operator)}
                    sx={{ mr: -1 }}
                  >
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                fontSize: "13px",
                cursor: "pointer",
                "& .MuiInputBase-input": { cursor: "pointer" }
              }}
              onClick={(e) => handleOpenDesignationPanel(e, id, value, operator)}
            />

            {designationPanelOpen === id && (
              <Popover
                open={true}
                anchorEl={designationPanelAnchor}
                onClose={handleCloseDesignationPanel}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                disableScrollLock
                PaperProps={{
                  sx: {
                    borderRadius: "16px",
                    p: 2,
                    minWidth: 350,
                    maxWidth: 350,
                    maxHeight: "70vh",
                  },
                }}
              >
                {renderDesignationPanelContent(id)}
              </Popover>
            )}
          </Box>
        );

      case "CertExpirationDate":
        return (
          <TextField
            size="small"
            label={t("dataGrid.Date")}
            type="date"
            InputLabelProps={{ shrink: true }}
            value={value || ""}
            onChange={(e) => handleUpdateFilter(id, "value", e.target.value)}
            fullWidth
          />
        );

      case "ActionRequired":
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>{t("dataGrid.Status")}</InputLabel>
            <Select
              label={t("dataGrid.Status")}
              value={value ?? ""}
              onChange={(e) =>
                handleUpdateFilter(id, "value", e.target.value)
              }
            >
              <MenuItem value=""
                sx={{ fontSize: '13px' }}
              >-- {t("dataGrid.Select")} --</MenuItem>
              <MenuItem value="1"
                sx={{ fontSize: '13px' }}
              >{t("dataGrid.accountLocked")}</MenuItem>
              <MenuItem value="2"
                sx={{ fontSize: '13px' }}
              >{t("dataGrid.PendingConnection")}</MenuItem>
              <MenuItem value="3"
                sx={{ fontSize: '13px' }}
              >{t("dataGrid.noCertifications")}</MenuItem>
              <MenuItem value="4"
                sx={{ fontSize: '13px' }}
              >{t("dataGrid.missingPhoto")}</MenuItem>
            </Select>
          </FormControl>
        );

      default:
        return (
          <TextField
            sx={{
              width: { xs: "auto", sm: 200 },
              maxWidth: { xs: 150, sm: "none" },
            }}
            size="small"
            label={t("dataGrid.Value")}
            value={value || ""}
            onChange={(e) => handleUpdateFilter(id, "value", e.target.value)}
            fullWidth
            placeholder={t("dataGrid.EnterValue")}
          />
        );
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpenFilterMenu}
        size="small"
        title="Open Filter Panel"
        sx={{ color: localFilters.length > 0 ? "#e37e33" : "inherit" }}
      >
        <FilterListIcon />
        {localFilters.length >= 1 && (
          <Chip
            label={localFilters.length}
            size="small"
            sx={{
              position: "absolute",
              top: -4,
              right: -4,
              height: "20px",
              width: "20px",
              backgroundColor: "#e37e33",
              color: "white",
              fontSize: "0.7rem",
            }}
          />
        )}
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseFilterMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock
      >
        <Box sx={{ p: 2, width: { xs: "100%", sm: 550 }, maxHeight: "75vh", overflow: "auto" }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t("dataGrid.FilterRecords")}
          </Typography>

          {/* Filter Rows */}
          {localFilters.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t("dataGrid.NofiltersTxt")}
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ mb: 2 }}>
              {localFilters.map((filter) => {

                const availableOptionsForThisRow = fieldOptions.filter(opt => {
                  const isUsedElsewhere = usedFields.includes(opt.value);
                  const isCurrentValue = opt.value === filter.field;
                  return !isUsedElsewhere || isCurrentValue;
                });

                return (
                  <Box
                    key={filter.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1.3fr 1fr 1fr auto",
                        sm: "1.3fr 1fr 1.5fr auto",
                      },
                      gap: 1,
                      alignItems: "flex-start",
                      p: 1.5,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#fafafa",
                      "& .MuiInputBase-root": { fontSize: "0.82rem" },
                      "& .MuiInputLabel-root": { fontSize: "0.78rem" },
                      "& .MuiSelect-select": { fontSize: "0.82rem" },
                      "& .MuiAutocomplete-input": { fontSize: "0.82rem" },
                    }}
                  >
                    <FormControl size="small" fullWidth>
                      <InputLabel>{t("dataGrid.Field")}</InputLabel>
                      <Select
                        label="Field"
                        value={filter.field}
                        onChange={(e) =>
                          handleUpdateFilter(filter.id, "field", e.target.value)
                        }
                      >
                        {availableOptionsForThisRow.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}
                            sx={{ fontSize: '13px' }}
                          >
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth>
                      <InputLabel>{t("dataGrid.Operator")}</InputLabel>
                      <Select
                        label="Operator"
                        value={filter.operator}
                        onChange={(e) =>
                          handleUpdateFilter(
                            filter.id,
                            "operator",
                            e.target.value
                          )
                        }

                      >
                        {(OPERATOR_MAP[filter.field] || []).map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}
                            sx={{ fontSize: '13px' }}
                          >
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box>
                      {renderValueInput(filter)}
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFilter(filter.id)}
                      sx={{ color: "error.main", mt: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>
          )}

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{ display: "flex", gap: 1, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, }}
          >
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddFilter}
              disabled={localFilters.length >= fieldOptions.length}
              variant="outlined"
              size="small"
              sx={{
                width: { xs: "100%", sm: "auto" },
                fontSize: "12px",
                color: "#a56604",
                "&:hover": {
                  borderColor: "#a56604",
                },
                "&:disabled": {
                  borderColor: "#e0e0e0",
                  color: "#9e9e9e"
                }
              }}
            >
              {t("dataGrid.AddFilter")}
            </Button>

            <Box sx={{ display: "flex", gap: 1 }}>
              {localFilters.length > 0 && (
                <Button
                  onClick={handleClearAllFilters}
                  variant="text"
                  size="small"
                  color="error"
                >
                  {t("dataGrid.ClearAll")}
                </Button>
              )}
              <Button
                onClick={
                  localFilters.length > 0
                    ? handleApplyFilters
                    : handleCloseFilterMenu
                }
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#e37e33",
                }}
              >
                {localFilters.length > 0 ? t("dataGrid.Apply") : t("dataGrid.Close")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default CertConnectCustomFilterModal;